import type { PaddleGlobal, PaddleProps } from '@/types'

// Paddle.js 从 CDN 加载，而且**只在定价页按需注入**——不进全站 layout。
// 其余每一页都不该为一个只有这里用得到的第三方脚本多付一次请求。
//
// 仓库里没有 config/initializers/content_security_policy.rb，所以不需要为这个
// 域名开 CSP 白名单。将来若加了 CSP，script-src 要放 https://cdn.paddle.com。
const PADDLE_JS = 'https://cdn.paddle.com/paddle/v2/paddle.js'

let loading: Promise<PaddleGlobal> | null = null

// 幂等：Inertia 换页回来不会重复注入脚本，也不会重复 Initialize
// （重复 Initialize 会让 Paddle 在控制台告警）。
export function loadPaddle(paddle: PaddleProps): Promise<PaddleGlobal> {
  if (loading) return loading

  const token = paddle.client_token

  loading = new Promise<PaddleGlobal>((resolve, reject) => {
    if (!token) {
      reject(new Error('[paddle] client_token 未配置'))
      return
    }

    const init = () => {
      const sdk = window.Paddle
      if (!sdk) {
        reject(new Error('[paddle] 脚本已加载但 window.Paddle 不存在'))
        return
      }
      // Environment.set 必须在 Initialize **之前**调，且只在沙盒时调——
      // 生产是默认值，多调一次没有意义。
      if (paddle.environment === 'sandbox') sdk.Environment.set('sandbox')
      sdk.Initialize({ token })
      resolve(sdk)
    }

    // 脚本可能已经在（热更新、或同一次会话里第二次进这一页）。
    if (window.Paddle) {
      init()
      return
    }

    const script = document.createElement('script')
    script.src = PADDLE_JS
    script.async = true
    script.onload = init
    script.onerror = () => {
      // 失败要把 promise 释放掉，否则用户再点一次会拿到同一个已 reject 的 promise，
      // 网络恢复了也永远不会好。
      loading = null
      reject(new Error('[paddle] 脚本加载失败'))
    }
    document.head.appendChild(script)
  })

  return loading
}

export function openCheckout(sdk: PaddleGlobal, options: {
  priceId: string
  email: string
  userId: number
}): void {
  sdk.Checkout.open({
    items: [ { priceId: options.priceId, quantity: 1 } ],
    customer: { email: options.email },
    // 这是把 Paddle 订阅绑回我们 User 的**唯一**桥梁，webhook 从
    // data.custom_data.user_id 读回来。
    //
    // 它来自客户端，因此**可被篡改**。实际危害是「自己付钱给别人开会员」——
    // 没有可利用价值（反向篡改只会让自己没订阅），而凭空造单由 webhook 的
    // HMAC 签名挡住。所以这里不做「服务端预建 customer」那一次额外 API 调用。
    // 别把这当漏洞重新讨论一遍，理由同 Paddle::EventProcessor#resolve_user。
    customData: { user_id: String(options.userId) },
  })
}
